import type { NewsItemDto, NewsSourceDto, NewsSourceTestResponse, SymbolMap } from '@et/shared';
import { useEffect, useState } from 'react';
import { api, describeError } from '../api/client';
import { dateTime } from '../lib/format';
import { t, useT } from '../lib/i18n';
import { toast } from '../lib/toast';
import { useResource } from '../lib/use-resource';

function health(h: boolean | null): JSX.Element {
  return h === null ? <span className="chip">{t('not polled')}</span> : h ? <span className="chip good"><span className="dot" /> {t('healthy')}</span> : <span className="chip critical"><span className="dot" /> {t('failing')}</span>;
}

export function News(): JSX.Element {
  const sources = useResource<NewsSourceDto[]>('/news/sources');
  const news = useResource<NewsItemDto[]>('/news?limit=100');
  const map = useResource<SymbolMap>('/news/symbol-map');
  const [test, setTest] = useState<NewsSourceTestResponse | null>(null);
  const [mapText, setMapText] = useState('');
  const tr = useT();
  useEffect(() => { if (map.data) setMapText(JSON.stringify(map.data, null, 2)); }, [map.data]);

  const update = async (key: string, body: object, ok: string): Promise<void> => {
    try {
      sources.setData(await api.put<NewsSourceDto[]>(`/news/sources/${key}`, body));
      toast.success(ok);
    } catch (err) {
      toast.error(describeError(err));
    }
  };

  return (
    <div className="stack">
      <h1>{tr('News sources')}</h1>
      {sources.data?.map((s) => (
        <div key={s.key} className="card stack">
          <div className="row">
            <h2 style={{ margin: 0 }}>{s.key === 'rss' ? tr('RSS feeds') : tr('Binance announcements')}</h2>
            {health(s.healthy)}
            <span className="muted">{tr('{n} new today · last poll {time}', { n: s.itemsToday, time: dateTime(s.lastPollAt) })}</span>
            <span style={{ flex: 1 }} />
            <label className="row"><input type="checkbox" checked={s.enabled} onChange={(e) => void update(s.key, { enabled: e.target.checked }, t(e.target.checked ? 'Source enabled' : 'Source disabled'))} /> {tr('enabled')}</label>
            <button className="small" onClick={() => void api.post<NewsSourceTestResponse>(`/news/sources/${s.key}/test`).then(setTest, (err: unknown) => { toast.error(describeError(err)); })}>{tr('Test fetch')}</button>
          </div>
          <label className="field" style={{ maxWidth: 200 }}>{tr('Poll interval (s)')}
            <input type="number" defaultValue={s.pollIntervalSec} onBlur={(e) => { if (Number(e.target.value) !== s.pollIntervalSec) void update(s.key, { params: { pollIntervalSec: Number(e.target.value) } }, t('Interval saved')); }} />
          </label>
          {s.key === 'rss' && (
            <table><tbody>{s.feeds.map((f) => (
              <tr key={f.name}>
                <td><input type="checkbox" checked={f.enabled} onChange={(e) => void update('rss', { params: { feeds: s.feeds.map((x) => ({ name: x.name, url: x.url, enabled: x.name === f.name ? e.target.checked : x.enabled })) } }, t(e.target.checked ? '{name} on' : '{name} off', { name: f.name }))} /></td>
                <td>{f.name}</td><td className="muted mono">{f.url}</td><td>{health(f.healthy)}</td>
              </tr>
            ))}</tbody></table>
          )}
          {s.key === 'binance_announcements' && (
            <label className="field" style={{ maxWidth: 360 }}>{tr('On a delisting of a coin we hold')}
              <select value={s.delistingAction} onChange={(e) => void update(s.key, { params: { delistingAction: e.target.value } }, t('Delisting action saved'))}>
                <option value="close">{tr('close the position at market')}</option>
                <option value="block_only">{tr('block entries only, keep the position')}</option>
              </select>
            </label>
          )}
        </div>
      ))}

      {test && (
        <div className="card">
          <div className="row"><h2 style={{ margin: 0 }}>{tr('Test fetch')} {test.ok ? '' : tr('— failed')}</h2><span style={{ flex: 1 }} /><button className="small" onClick={() => { setTest(null); }}>{tr('Close')}</button></div>
          {test.detail && <div className="neg">{test.detail}</div>}
          <table><tbody>{test.items.map((i) => <tr key={i.title}><td className="muted">{dateTime(i.publishedAt)}</td><td>{tr(i.category)}</td><td className="mono">{i.symbols.join(', ')}</td><td style={{ whiteSpace: 'normal' }}>{i.title}</td></tr>)}</tbody></table>
        </div>
      )}

      <div className="card stack">
        <h2>{tr('Symbol map')}</h2>
        <div className="muted">{tr('Name → ticker aliases, and tickers that are also common words (matched only as $CASHTAG or via an alias).')}</div>
        <textarea className="mono" rows={10} value={mapText} onChange={(e) => { setMapText(e.target.value); }} />
        <div><button className="primary" onClick={() => void (async () => {
          try {
            await api.put('/news/symbol-map', JSON.parse(mapText) as SymbolMap);
            toast.success(t('Symbol map saved'));
          } catch (err) {
            toast.error(err instanceof SyntaxError ? t('not valid JSON') : describeError(err));
          }
        })()}>{tr('Save')}</button></div>
      </div>

      <div className="card">
        <h2>{tr('Recent news')}</h2>
        <div className="table-wrap"><table>
          <thead><tr><th>{tr('Published')}</th><th>{tr('Source')}</th><th>{tr('Category')}</th><th>{tr('Symbols')}</th><th>{tr('Title')}</th></tr></thead>
          <tbody>{news.data?.map((n) => (
            <tr key={n.id} className={n.duplicateOf ? 'muted' : ''}>
              <td className="muted">{dateTime(n.publishedAt)}</td><td>{n.provider}</td><td>{tr(n.category)}</td><td className="mono">{n.symbols.join(', ')}</td>
              <td style={{ whiteSpace: 'normal' }}>{n.url ? <a href={n.url} target="_blank" rel="noreferrer">{n.title}</a> : n.title}{n.duplicateOf ? ` ${tr('(duplicate)')}` : ''}</td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>
    </div>
  );
}
